import Link from "next/link";

import { useStoreContext } from "../../contexts/StoreDataContext";
import useStorefrontLegal from "../../hooks/useStorefrontLegal";
import { resolveImage } from "../../lib/media";
import Picture from "../Picture";
import BackToTopSvg from "./BackToTopSvg";

const LEGAL_LINKS = [
  { href: "/privacy-policy", label: "Privacy", field: "policy" },
  { href: "/shipping-policy", label: "Shipping & Returns", field: "returns" },
  { href: "/terms-and-conditions", label: "Terms & Conditions", field: "terms" },
];

const FeaturedFooter = () => {
  const context = useStoreContext();
  const storeData = context?.storeData;
  const { data: legalInfo } = useStorefrontLegal(Boolean(storeData));
  const footerLinks = [
    { href: "/contact", label: "Info" },
    ...LEGAL_LINKS.filter(
      ({ field }) => legalInfo?.[field] != null && String(legalInfo[field]).trim() !== "",
    ),
  ];
  const logo = storeData?.logo;
  const rawLogo = typeof logo === "string" ? logo : logo?.src || "";
  const logoSrc = rawLogo.startsWith("//") ? `https:${rawLogo}` : rawLogo;
  const resolved = resolveImage(logoSrc, typeof logo === "object" ? logo : null, "logo");

  const instagramHandle = (storeData?.social || "").trim().replace(/^@/, "");

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="mb-11 mt-[60px] w-full font-montserrat text-checkoutText">
      <div className="px-5">
        <div className="h-px bg-[#E8E5DC]" />
      </div>

      {/* Space between divider and footer content — change pt-8 here */}
      <div className="px-5 pt-8">
        <div className="flex flex-col items-center">
          {logoSrc ? (
            <Link href="/">
              <a aria-label="Home" className="block h-[70px] w-[70px] overflow-hidden rounded-full">
                {resolved.isV2 ? (
                  <Picture
                    resolved={resolved}
                    alt=""
                    width={70}
                    height={70}
                    imgClassName="h-full w-full object-cover"
                  />
                ) : (
                  <img
                    src={resolved.src}
                    alt=""
                    width={70}
                    height={70}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      if (e.currentTarget.src !== logoSrc) {
                        e.currentTarget.src = logoSrc;
                      }
                    }}
                  />
                )}
              </a>
            </Link>
          ) : null}

          <div className="mt-4 text-center text-xs font-normal tracking-[1px]">
            {storeData?.name}
          </div>

          {storeData?.email ? (
            <a
              href={`mailto:${storeData.email}`}
              className="mt-1 text-center text-[11px] font-normal tracking-[1px] text-checkoutText"
            >
              {storeData.email}
            </a>
          ) : null}

          {instagramHandle ? (
            <a
              href={`https://www.instagram.com/${instagramHandle}`}
              target="_blank"
              rel="noreferrer"
              className="mt-1 text-center text-[11px] font-normal tracking-[1px] text-checkoutText"
            >
              @{instagramHandle}
            </a>
          ) : null}
        </div>

        <div className="relative mt-8">
          <div className="flex flex-col items-start gap-4">
            {footerLinks.map((item) => (
              <Link key={item.href} href={item.href}>
                <a className="m-0 inline-block w-fit text-[11px] font-normal tracking-[1px] text-checkoutText">
                  {item.label}
                </a>
              </Link>
            ))}
          </div>

          <button
            type="button"
            aria-label="Back to top"
            className="absolute bottom-0 right-0 z-10 flex h-8 w-8 cursor-pointer items-end justify-end border-0 bg-transparent p-0"
            onClick={scrollToTop}
          >
            <BackToTopSvg />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default FeaturedFooter;
